import { useState } from 'react';
import { Copy, KeyRound, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  formatMessageTime, supportApi, toastApiError,
} from '../../lib/support';
import { enterCustomerAccount } from '../../lib/supportSession';

/**
 * Account access on an onboarding customer's card in Support: go into their
 * account, or make an agent token for importing, and what we've done there.
 * Buying onboarding is their consent; it ends at Done, or when they stop it.
 */
export function AccountAccessPanel({ thread, region }) {
  const access = thread.account_access || {};
  const [opening, setOpening] = useState(false);
  const [making, setMaking] = useState(false);
  const [token, setToken] = useState(null);

  const open = async () => {
    setOpening(true);
    try {
      await enterCustomerAccount(thread.id);
    } catch (e) {
      toastApiError(e, 'Could not open their account');
      setOpening(false);
    }
  };

  const makeToken = async () => {
    setMaking(true);
    try {
      setToken(await supportApi.agentToken(thread.id));
    } catch (e) {
      toastApiError(e, 'Could not make a token');
    } finally {
      setMaking(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error('Could not copy. Select it and copy by hand.');
    }
  };

  return (
    <div className="space-y-3" data-testid="account-access">
      <p className="eyebrow">account access</p>
      {access.open ? (
        <div className="flex flex-col gap-2">
          <Button size="sm" onClick={open} disabled={opening} data-testid="open-account">
            {opening ? <Loader2 className="animate-spin" /> : <LogIn />}
            Open their account
          </Button>
          <Button size="sm" variant="outline" onClick={makeToken} disabled={making} data-testid="agent-token">
            {making ? <Loader2 className="animate-spin" /> : <KeyRound />}
            Make an agent token
          </Button>
          <p className="text-xs text-muted-foreground">
            For an hour at a time. They&rsquo;re told when you go in; every change is logged below.
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{access.why_not}</p>
      )}

      {access.log?.length > 0 && (
        <ol className="text-sm space-y-1.5 max-h-64 overflow-y-auto" data-testid="access-log">
          {access.log.map((row, i) => (
            <li key={`${row.at}-${i}`} className="flex justify-between gap-3">
              <span className="min-w-0">
                <span className={row.status >= 400 ? 'text-muted-foreground line-through' : ''}>{row.description}</span>
                {row.admin_name && <span className="text-muted-foreground"> &middot; {row.admin_name.split(' ')[0]}</span>}
              </span>
              <span className="text-muted-foreground tabular-nums shrink-0 text-xs pt-0.5">
                {formatMessageTime(row.at, region)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <Dialog open={!!token} onOpenChange={(v) => { if (!v) setToken(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agent token</DialogTitle>
            <DialogDescription>
              Shown once. It works only through the MCP tools, can&rsquo;t read their settings, and stops when
              onboarding is done, when they stop access, or on{' '}
              {token && new Date(token.expires_at).toLocaleDateString()}.
            </DialogDescription>
          </DialogHeader>
          {token && (
            <div className="space-y-4 min-w-0">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Token</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(token.token, 'Token')}><Copy />Copy</Button>
                </div>
                <code className="block rounded-cards bg-mist p-3 text-sm break-all">{token.token}</code>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Agent config</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(JSON.stringify(token.config, null, 2), 'Config')}>
                    <Copy />Copy
                  </Button>
                </div>
                <pre className="rounded-cards bg-mist p-3 text-xs overflow-x-auto">{JSON.stringify(token.config, null, 2)}</pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setToken(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
